import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useLike } from '../hooks/useLike'
import OnlineDot from './OnlineDot'
import { decodeEntities } from '../lib/decodeEntities'

// Palette cycles for cover gradients and avatars
const COVER_GRADIENTS = [
  'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
  'linear-gradient(135deg, #0D9488 0%, #7C3AED 100%)',
  'linear-gradient(135deg, #EC4899 0%, #F97316 100%)',
  'linear-gradient(135deg, #1D4ED8 0%, #7C3AED 100%)',
  'linear-gradient(135deg, #059669 0%, #14B8A6 100%)',
  'linear-gradient(135deg, #BE123C 0%, #EC4899 100%)',
]

const AVATAR_COLORS = [
  'linear-gradient(135deg, #7C3AED, #EC4899)',
  'linear-gradient(135deg, #0D9488, #1D4ED8)',
  'linear-gradient(135deg, #EA580C, #EC4899)',
  'linear-gradient(135deg, #059669, #0D9488)',
  'linear-gradient(135deg, #BE123C, #7C3AED)',
  'linear-gradient(135deg, #1D4ED8, #14B8A6)',
]

const RATING_COLORS = {
  Teen:   { bg: '#059669', color: '#fff' },
  Mature: { bg: '#DC2626', color: '#fff' },
}

const GENRE_COLORS = {
  Fantasy:   '#7C3AED',
  Romance:   '#EC4899',
  'Sci-Fi':  '#0D9488',
  Anime:     '#EA580C',
  Mystery:   '#059669',
  Horror:    '#BE123C',
  Adventure: '#1D4ED8',
  Drama:     '#9333EA',
  Other:     '#6B7280',
  Default:   '#6B7280',
}

function formatNum(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

const THREE_DAYS_NEW  = 3 * 24 * 60 * 60 * 1000

function stripMarkdown(text) {
  if (!text) return ''
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
}

function tagClass(tag, story) {
  if (tag === story.fandom) return 'story-card__tag--fandom'
  if ((story.genre ?? []).includes(tag)) return 'story-card__tag--genre'
  if (['Teen', 'Mature', 'General'].includes(tag)) return 'story-card__tag--rating'
  return 'story-card__tag--trope'
}
const THREE_DAYS  = 3 * 24 * 60 * 60 * 1000

function getStoryBadge(createdAt, lastChapterAt) {
  const now = Date.now()
  const created = new Date(createdAt).getTime()
  if (!lastChapterAt) {
    return now - created < THREE_DAYS_NEW ? 'new' : null
  }
  const lastChapter = new Date(lastChapterAt).getTime()
  const isNew = now - created < THREE_DAYS_NEW
  const isUpdated = now - lastChapter < THREE_DAYS && lastChapter - created > 60000
  if (isNew && isUpdated) return 'updated'
  if (isNew) return 'new'
  if (isUpdated) return 'updated'
  return null
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  return `${Math.floor(days / 30)}mo ago`
}

export default function StoryCard({ story, index = 0, onDelete = null, onGenreClick = null, onFandomClick = null, progress = null }) {
  const [confirming, setConfirming] = useState(false)
  const [deleting,   setDeleting]   = useState(false)
  const { liked, count: likeCount, toggle: toggleLike } = useLike('story', story.id, story.likes, story.likedByMe)
  const navigate = useNavigate()

  const badge       = getStoryBadge(story.createdAt, story.lastChapterAt)
  const gradient    = COVER_GRADIENTS[index % COVER_GRADIENTS.length]
  const avatarColor = story.authorAvatarGrad ?? AVATAR_COLORS[index % AVATAR_COLORS.length]
  const avatarUrl   = story.authorAvatarUrl ?? null
  const genreColor  = GENRE_COLORS[story.genre?.[0]] ?? GENRE_COLORS.Default

  async function handleDelete(e) {
    e.preventDefault()
    if (!confirming) { setConfirming(true); return }
    setDeleting(true)
    await onDelete(story.id)
  }

  return (
    <div className="story-card-wrap">
      <div onClick={() => navigate(story.published === false ? `/story/${story.id}/edit` : `/story/${story.id}`)} style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}>
        <article className="story-card">
          {/* Cover / Banner */}
          <div className="story-card__cover">
            {story.bannerUrl
              ? <img src={story.bannerUrl} alt={story.title} className="story-card__cover-img" loading="lazy" />
              : story.coverUrl
                ? <img src={story.coverUrl} alt={story.title} className="story-card__cover-img" loading="lazy" />
                : <div className="story-card__cover-gradient" style={{ background: gradient }} />
            }
            <div className="story-card__cover-overlay" />
            {story.rating && story.rating !== 'General' &&
             !(story.sexuallyExplicit && story.rating === 'Mature') && (
              <span
                className="story-card__rating-badge"
                style={{ background: RATING_COLORS[story.rating]?.bg ?? '#6B7280', color: RATING_COLORS[story.rating]?.color ?? '#fff' }}
              >
                {story.rating}
              </span>
            )}
            {story.sexuallyExplicit && (
              <span className="story-card__explicit-badge">Sexually Explicit</span>
            )}
            {onFandomClick ? (
              <button
                className="story-card__fandom-badge story-card__fandom-badge--btn"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFandomClick(story.fandom) }}
                title={`Filter by ${story.fandom}`}
              >
                {decodeEntities(story.fandom)}
              </button>
            ) : (
              <span className="story-card__fandom-badge">{decodeEntities(story.fandom)}</span>
            )}
            {story.status && (
              <span className="story-card__status-badge" data-status={story.status.toLowerCase()}>
                {story.status}
              </span>
            )}
            <div className="story-card__cover-stats">
              <span>{story.chapters ?? 0} ch</span>
            </div>
          </div>

          {/* Body */}
          <div className="story-card__body">
            <div className="story-card__author-row">
              {story.authorUsername ? (
                <Link
                  to={`/user/${story.authorUsername}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'contents', textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="story-card__avatar" style={{ position: 'relative', background: avatarUrl ? undefined : avatarColor }}>
                    {avatarUrl
                      ? <img src={avatarUrl} alt={story.author} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} loading="lazy" />
                      : story.author?.[0]?.toUpperCase() ?? '?'}
                    <OnlineDot userId={story.userId} size={8} fallback={story.authorIsOnline} />
                  </div>
                  <span className="story-card__author">{decodeEntities(story.author)}</span>
                </Link>
              ) : (
                <>
                  <div className="story-card__avatar" style={{ position: 'relative', background: avatarUrl ? undefined : avatarColor }}>
                    {avatarUrl
                      ? <img src={avatarUrl} alt={story.author} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} loading="lazy" />
                      : story.author?.[0]?.toUpperCase() ?? '?'}
                    <OnlineDot userId={story.userId} size={8} fallback={story.authorIsOnline} />
                  </div>
                  <span className="story-card__author">{decodeEntities(story.author)}</span>
                </>
              )}
              <span className="story-card__updated">{story.lastChapterAt && new Date(story.lastChapterAt).getTime() - new Date(story.createdAt).getTime() > 60000 ? `Updated ${timeAgo(story.lastChapterAt)}` : timeAgo(story.createdAt)}</span>
            </div>

            <div className="story-card__title-row">
              <h3 className="story-card__title">{decodeEntities(story.title)}</h3>
              {badge === 'new'     && <span className="story-card__badge story-card__badge--new">New!</span>}
              {badge === 'updated' && <span className="story-card__badge story-card__badge--updated">Updated</span>}
            </div>
            <p className="story-card__desc">{decodeEntities(stripMarkdown(story.description))}</p>

            {(story.sexuallyExplicit || story.tags?.length > 0) && (
              <div className="story-card__tags">
                {story.sexuallyExplicit && (
                  <span className="story-card__tag story-card__tag--rating">Explicit</span>
                )}
                {story.tags?.slice(0, 3).map(tag => (
                  <span key={tag} className={`story-card__tag ${tagClass(tag, story)}`}>#{decodeEntities(tag)}</span>
                ))}
              </div>
            )}

            <div className="story-card__footer">
              {onGenreClick && story.genre?.[0] ? (
                <button
                  className="story-card__stat story-card__stat--genre-btn"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onGenreClick(story.genre[0]) }}
                  title={`Filter by ${story.genre[0]}`}
                >
                  <div className="story-card__genre-dot" style={{ background: genreColor }} />
                  {decodeEntities(story.genre[0])}
                </button>
              ) : (
                <span className="story-card__stat">
                  <div className="story-card__genre-dot" style={{ background: genreColor }} />
                  {story.genre?.[0] ?? 'Fiction'}
                </span>
              )}
              <button
                className={`story-card__stat story-card__like-btn${liked ? ' story-card__like-btn--liked' : ''}`}
                onClick={toggleLike}
                aria-label={liked ? 'Unlike' : 'Like'}
              >
                {liked ? '❤️' : '🤍'} {formatNum(likeCount)}
              </button>
              <span className="story-card__stat" style={{ marginLeft: 'auto' }}>
                {formatNum(story.words)} w
              </span>
            </div>
            {progress != null && (
              <div className="story-card__progress">
                <div className="story-card__progress-bar" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        </article>
      </div>

      {onDelete && (
        <div className="story-card__delete-row">
          {confirming ? (
            <>
              <span className="story-card__delete-confirm-text">Delete this story?</span>
              <button
                className="story-card__delete-btn story-card__delete-btn--confirm"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button
                className="story-card__delete-btn story-card__delete-btn--cancel"
                onClick={e => { e.preventDefault(); setConfirming(false) }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button className="story-card__delete-btn" onClick={handleDelete}>
              Delete story
            </button>
          )}
        </div>
      )}
    </div>
  )
}
